import { z } from 'zod';
import { paginationSchema } from '@/types/pagination';
import { type GalleryView } from '@/types';
import { nodeSchema } from '@/types/tree';

export const trashNodesResponseSchema = z.object({
  nodes: z.array(z.string()),
});

export type TrashNodesResponse = z.infer<typeof trashNodesResponseSchema>;

type TrashOrRestoreNodesParams = {
  nodes: Array<string>;
  type?: string;
  undoable?: boolean;
  assetIds?: Array<string>;
};

export type TrashNodesFn = (params: TrashOrRestoreNodesParams) => Promise<TrashNodesResponse>;

export type RestoreNodesFn = (params: TrashOrRestoreNodesParams) => Promise<TrashNodesResponse>;

export const trashRootNodeSchema = z.object({
  id: z.string(),
  type: z.string().optional().nullable(),
  node: nodeSchema,
  width: z.number().nullable().optional(),
  height: z.number().nullable().optional(),
  rating: z.number().optional(),
  commentCount: z.number().optional().nullable(),
  variants: z
    .union([
      z.tuple([]),
      z.record(
        z.string(),
        z.object({
          width: z.number(),
          height: z.number(),
          url: z.string(),
          type: z.string().optional().nullable(),
          name: z.string().optional().nullable(),
          watermark: z.string().optional().nullable(),
          automatic: z.boolean().optional().nullable(),
          public: z.boolean().optional().nullable(),
          format: z.string().optional().nullable(),
        }),
      ),
    ])
    .optional()
    // api will return an empty array if there are no variants,
    // transform it into an empty object instead, to make it easier to work with
    .transform((arg) => (Array.isArray(arg) || arg === undefined ? {} : arg)),
  // Client-side only, never sent by the API. Set when a broadcast lands for a row still on
  // screen, and cleared by the refresh that removes the row. Same shape as the Asset
  // schema's, so a trash row maps straight onto the gallery's asset shape.
  //
  // In the bin a row departs by being restored - it still exists, so it stays selectable.
  // `deleted` is permanent and wins, clearing `departed` when it is set.
  departed: z.enum(['trashed', 'restored']).optional(),
  deleted: z.boolean().optional(),
});
export type TrashRootNode = z.infer<typeof trashRootNodeSchema>;

export const getTrashResponseSchema = z.object({
  trash: z.array(trashRootNodeSchema),
  pagination: paginationSchema,
});

export type GetTrashResponse = z.infer<typeof getTrashResponseSchema>;

export type TrashCardProps = {
  root: TrashRootNode;
  className?: string;
  isSelected: boolean;
  onClick: () => void;
  view?: GalleryView;
};
